import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Ban, ShieldMinus, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'
import { fetchBotsForMod, restrictBot, BOT_MOD_STR as S, type BotModRow } from './moderationApi'

/**
 * A5.11 — Botni cheklash (restrict). LIST = REAL GET /admin/bots. Cheklash/olib
 * tashlash = REAL POST /admin/bots/:id {revoked} (adminH.EditBot). Cheklangan bot
 * xabar yubora olmaydi. Bloklashdan (A5.12) yumshoqroq, qaytariladigan chora.
 */
export function RestrictBotScreen() {
  const { toast } = useToast()
  const [target, setTarget] = useState<{ row: BotModRow; restrict: boolean } | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const botLabel = (r: BotModRow) => (r.username ? `@${r.username}` : r.first_name || `#${r.id}`)

  const columns = useMemo<AdminColumn<BotModRow>[]>(
    () => [
      {
        key: 'username',
        header: S.bot,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <Link to={`/bots/${r.id}`} className="text-accent hover:underline">
            {botLabel(r)}
          </Link>
        ),
      },
      { key: 'first_name', header: S.name, type: 'text', minWidth: 160 },
      {
        key: 'revoked',
        header: S.status,
        type: 'text',
        width: 140,
        render: (r) =>
          r.revoked ? (
            <StatusBadge status="banned" label={S.restricted} />
          ) : (
            <StatusBadge status="active" label={S.active} />
          ),
      },
      { key: 'created_at', header: S.registered, type: 'date', width: 150 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<BotModRow>[]>(
    () => [
      {
        id: 'restrict',
        label: S.restrict,
        icon: <ShieldMinus size={13} />,
        danger: true,
        visible: (r) => !r.revoked,
        onSelect: (r) => setTarget({ row: r, restrict: true }),
      },
      {
        id: 'unrestrict',
        label: S.unrestrict,
        icon: <ShieldCheck size={13} />,
        visible: (r) => Boolean(r.revoked),
        onSelect: (r) => setTarget({ row: r, restrict: false }),
      },
    ],
    [],
  )

  const fetcher = useCallback(fetchBotsForMod, [])

  return (
    <>
      <PageHeader title={S.restrictTitle} screenId="A5.11" />
      <DataTable<BotModRow>
        key={reloadKey}
        screenId="A5.11"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Ban size={28} strokeWidth={1.5} />, title: S.empty }}
      />

      {target && (
        <DangerDialog
          open
          onClose={() => setTarget(null)}
          screenId="A5.11"
          level={2}
          title={target.restrict ? S.restrictConfirmTitle : S.unrestrictConfirmTitle}
          description={target.restrict ? S.restrictConfirmDesc : S.unrestrictConfirmDesc}
          confirmLabel={target.restrict ? S.restrict : S.unrestrict}
          onConfirm={async () => {
            try {
              await restrictBot(target.row.id, target.restrict)
              toast(S.restrictDone, { tone: 'success' })
              setTarget(null)
              setReloadKey((k) => k + 1)
            } catch (e) {
              toast(S.failed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}
    </>
  )
}
